<template>
  <div class="settings-view">
    <PageHeader title="Projects" subtitle="Spaces for organising documents." />
    <div v-if="message" class="workspace-alert">{{ message }}</div>
    <SettingsSection v-if="canCreateProject" title="Create project">
      <div class="inline-form">
        <input v-model="projectName" placeholder="Project name" @keyup.enter="createProject" />
        <button type="button" class="btn btn-primary" @click="createProject">Create</button>
      </div>
    </SettingsSection>
    <AsyncState :loading="!projects" :empty="(projects?.projects?.length ?? 0) === 0" empty-label="No projects yet.">
      <div class="access-card">
        <div class="admin-table-wrap access-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Project</th>
                <th v-if="canManageProjectAccess" class="table-actions-col">Access</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects?.projects ?? []" :key="project.id">
                <td>
                  <span class="project-cell">
                    <FolderClosed class="icon" />
                    {{ project.name }}
                  </span>
                </td>
                <td v-if="canManageProjectAccess" class="table-actions-col">
                  <RouterLink class="btn" :to="`/settings/projects/${project.id}/access`">Manage access</RouterLink>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </AsyncState>
  </div>
</template>

<script setup lang="ts">
import { FolderClosed } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import {
  CommandsService,
  PermissionKind,
  type GetAdminProjectsQueryResponse
} from '../api/generated';
import AsyncState from '../components/settings/AsyncState.vue';
import PageHeader from '../components/settings/PageHeader.vue';
import SettingsSection from '../components/settings/SettingsSection.vue';
import { getErrorMessage, useSessionStore } from '../stores/session';

const session = useSessionStore();
const projects = ref<GetAdminProjectsQueryResponse | null>(null);
const projectName = ref('');
const message = ref<string | null>(null);
const canCreateProject = computed(() => session.hasGlobalPermission(PermissionKind.CREATE_PROJECT));
const canManageProjectAccess = computed(() => session.hasGlobalPermission(PermissionKind.MANAGE_PROJECT_ACCESS));

onMounted(load);

async function load(): Promise<void> {
  try {
    projects.value = session.pageBootstrap?.adminProjects ?? null;
    if (projects.value == null) {
      throw new Error('Projects bootstrap payload was missing.');
    }

    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load projects.');
  }
}

async function createProject(): Promise<void> {
  const name = projectName.value.trim();
  if (!name) {
    return;
  }

  try {
    const result = await CommandsService.commandCreateProjectCommand({ requestBody: { name } });
    projects.value ??= { projects: [] };
    projects.value.projects ??= [];
    projects.value.projects.push(result);
    projects.value.projects.sort((left, right) => left.name.localeCompare(right.name));
    projectName.value = '';
    message.value = null;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to create project.');
  }
}
</script>
